import { useEffect, useRef, useState } from 'react';

import { matchesApiErrorStatus } from '@/shared/error/isZepQuizApiError';

const RECOVERABLE_CURSOR_ERRORS = [
  'INVALID_CURSOR',
  'EXPIRED_CURSOR',
  'CURSOR_QUERY_MISMATCH',
] as const;

export const isRecoverableCursorError = (error: unknown) =>
  matchesApiErrorStatus(error, [...RECOVERABLE_CURSOR_ERRORS]);

export const useCursorQueryRecovery = ({
  isError,
  error,
  hasLoadedPages,
  refetch,
}: {
  isError: boolean;
  error: unknown;
  hasLoadedPages: boolean;
  refetch: () => Promise<unknown>;
}) => {
  const attemptedRef = useRef(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const shouldRecover =
    isError && hasLoadedPages && isRecoverableCursorError(error);

  useEffect(() => {
    if (!isError) {
      attemptedRef.current = false;
      setIsRecovering(false);
      return;
    }
    if (!shouldRecover || attemptedRef.current) return;

    attemptedRef.current = true;
    setIsRecovering(true);
    void refetch().then(
      () => setIsRecovering(false),
      () => setIsRecovering(false),
    );
  }, [isError, refetch, shouldRecover]);

  return { isRecovering };
};
